import { useState, useRef, useEffect, SetStateAction, Dispatch } from 'react'
import { t } from '../utils/i18n'
import styles from './_announcement.module.scss'
import classnames from 'classnames'
import { useLazyQuery, useMutation } from '@apollo/client'
import { useOutsideClick } from '../utils/hooks/useOutsideClick'
import useErrorHandling from '../utils/hooks/useErrorHandling'
import { useAuth } from '../utils/auth'
import { ReactSVG } from 'react-svg'
import { MARK_ANNOUNCEMENTS_AS_READ_MUTATION } from '../gql/mutations/mark_announcements_as_read_mutation'
import {
  Announcement,
  CommunityConsolidatedNotification,
  User
} from '../__generated__/graphql'
import { routeType } from './AvatarDropdown'
import { useRouter } from 'next/router'
import { labPath } from '../utils/routeHelpers'
import { isTeamMember } from '../utils/isTeamMember'
import {
  COMMUNITY_CONSOLIDATED_NOTIFICATION_MARK_ALL_READ_MUTATION,
  COMMUNITY_PROFILE_CONSOLIDATED_NOTIFICATIONS_QUERY
} from '../gql'
import MainAnnouncementsDropdown from './MainAnnouncementsDropdown'
import LabAnnouncementsDropdown from './LabAnnouncementsDropdown'
import { getOperationName } from 'apollo-boost'
import LabBenefitAnnouncement from './LabBenefitAnnouncement'

interface DropdownProps {
  userId: number
  announcements: Announcement[] | CommunityConsolidatedNotification[]
  isRemoveReadFilter: boolean
  setIsRemoveReadFilter: Dispatch<SetStateAction<boolean>>
  activeMenu: routeType
  setActiveMenu: Dispatch<SetStateAction<routeType>>
  unreadLabAnnouncementsCount: number
  unreadMainAnnouncementsCount: number
  loadMoreLabAnnouncements?: () => void
  isLabLoading?: boolean
  setLabAnnouncements: Dispatch<
    SetStateAction<CommunityConsolidatedNotification[]>
  >
  showLabBenefit: boolean
}

interface NotificationBellProps {
  isAnyAnnouncements: boolean
  unreadAnnouncementCount: number
  onClick?: () => void
  isGeneral?: boolean
}

const PER_PAGE = 10

const NotificationBell: React.FC<NotificationBellProps> = ({
  isAnyAnnouncements,
  unreadAnnouncementCount,
  onClick
}) => {
  return (
    <div
      className={classnames(styles.notificationBell, {
        [styles.notificationBellWhite]: isAnyAnnouncements,
        [styles.notificationBellPurple]: unreadAnnouncementCount
      })}
      onClick={onClick}
    >
      <div>
        <ReactSVG src="/images/bell.svg" />
      </div>
      <span
        className={classnames(styles.notificationBellCircle, {
          [styles.displayFlex]: unreadAnnouncementCount > 0
        })}
      >
        {unreadAnnouncementCount}
      </span>
    </div>
  )
}

const AnnouncementButton: React.FC<{
  label: string
  count: number
  isActive: boolean
  onClick: () => void
}> = ({ label, count, isActive, onClick }) => (
  <button onClick={onClick} className={isActive ? styles.isActive : ''}>
    {count > 0 && <span className={styles.notificationCircle}>{count}</span>}
    <span className={styles.notificationType}>{label}</span>
  </button>
)

const Dropdown: React.FC<DropdownProps> = ({
  userId,
  announcements,
  isRemoveReadFilter,
  setIsRemoveReadFilter,
  activeMenu,
  setActiveMenu,
  unreadLabAnnouncementsCount,
  unreadMainAnnouncementsCount,
  loadMoreLabAnnouncements,
  isLabLoading,
  setLabAnnouncements,
  showLabBenefit
}) => {
  const notRead = announcements.filter((notice: Announcement) => !notice.read)

  const isAnyAnnouncements = Boolean(announcements.length)

  const errorHandling = useErrorHandling()
  const { currentUser, setCurrentUser } = useAuth()

  const [markAllMainAsRead] = useMutation(MARK_ANNOUNCEMENTS_AS_READ_MUTATION, {
    onError: errorHandling,
    update: (_cache, result) => {
      const { announcements } = result.data.markAnnouncementsAsRead
      setCurrentUser({
        ...currentUser,
        announcements
      } as User)
    }
  })

  const [markAllLabAsRead] = useMutation(
    COMMUNITY_CONSOLIDATED_NOTIFICATION_MARK_ALL_READ_MUTATION,
    {
      onCompleted: () => {
        setLabAnnouncements((announcements) =>
          announcements.map((a) => ({ ...a, read: true }))
        )
      },
      refetchQueries: [
        getOperationName(COMMUNITY_PROFILE_CONSOLIDATED_NOTIFICATIONS_QUERY)
      ]
    }
  )

  const markRead = () => {
    if (activeMenu === 'LAB') {
      markAllLabAsRead()
    } else {
      const notReadIds = (notRead as Announcement[]).map((notice) => notice.id)
      markAllMainAsRead({ variables: { announcementIds: notReadIds } })
    }
  }

  return (
    <section className={styles.notificationDropdown}>
      <div className={styles.notificationDropdownHeader}>
        <p>{t('Notifications')}</p>

        {isAnyAnnouncements && (
          <div className={styles.notificationDropdownFilters}>
            <span onClick={() => setIsRemoveReadFilter(!isRemoveReadFilter)}>
              {isRemoveReadFilter ? t('Show Read') : t('Hide Read')}
            </span>
            <span onClick={markRead}>{t('Mark All As Read')}</span>
          </div>
        )}
      </div>

      <div className={styles.announcementButtons}>
        <AnnouncementButton
          label="WORSHIP ONLINE"
          count={unreadMainAnnouncementsCount}
          isActive={activeMenu === 'MAIN'}
          onClick={() => setActiveMenu('MAIN')}
        />
        <AnnouncementButton
          label="LAB +"
          count={unreadLabAnnouncementsCount}
          isActive={activeMenu === 'LAB'}
          onClick={() => setActiveMenu('LAB')}
        />
      </div>

      <div className={styles.notificationItems}>
        {showLabBenefit && <LabBenefitAnnouncement />}
        {isAnyAnnouncements ? (
          activeMenu === 'LAB' ? (
            <LabAnnouncementsDropdown
              userId={userId}
              announcements={
                announcements as CommunityConsolidatedNotification[]
              }
              isRemoveReadFilter={isRemoveReadFilter}
              loadMoreAnnouncements={loadMoreLabAnnouncements}
              isLoading={isLabLoading}
              setLabAnnouncements={setLabAnnouncements}
            />
          ) : (
            <MainAnnouncementsDropdown
              announcements={announcements as Announcement[]}
              isRemoveReadFilter={isRemoveReadFilter}
            />
          )
        ) : (
          !showLabBenefit && (
            <div className={classnames(styles.notice, styles.noticeNoNotices)}>
              <p>{t('No New Notifications')}</p>
            </div>
          )
        )}
      </div>
    </section>
  )
}

const Announcements: React.FC = () => {
  const ref = useRef(null)
  const isLabFetchInProgress = useRef(false)

  const router = useRouter()
  const activeRoute: routeType = router.route.startsWith(labPath())
    ? 'LAB'
    : 'MAIN'

  const [activeMenu, setActiveMenu] = useState<routeType>(activeRoute)
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)
  const [isRemoveReadFilter, setIsRemoveReadFilter] = useState<boolean>(
    !!sessionStorage.getItem('isRemoveReadFilter') || false
  )

  const [labAnnouncements, setLabAnnouncements] = useState<
    CommunityConsolidatedNotification[]
  >([])
  const [page, setPage] = useState(1)

  useOutsideClick(ref, isDropdownOpen, () => {
    if (isDropdownOpen) setIsDropdownOpen(false)
  })

  useEffect(() => {
    if (isRemoveReadFilter) {
      sessionStorage.setItem('isRemoveReadFilter', 'isRemoveReadFilter')
    } else {
      sessionStorage.removeItem('isRemoveReadFilter')
    }
  }, [isRemoveReadFilter])

  const { currentUser } = useAuth()
  const mainAnnouncements = currentUser?.announcements

  const showLabBenefit =
    currentUser?.currentPlan &&
    currentUser?.labSubscriptionActive &&
    !currentUser?.labPlusBenefitConsumedAt &&
    !isTeamMember(currentUser)

  const [queryLabAnnouncements, { data }] = useLazyQuery(
    COMMUNITY_PROFILE_CONSOLIDATED_NOTIFICATIONS_QUERY
  )

  useEffect(() => {
    if (!currentUser?.labSubscriptionActive) {
      setLabAnnouncements([])
      setPage(1)
      return
    }

    queryLabAnnouncements({
      variables: { page, perPage: PER_PAGE },
      onCompleted: (data) => {
        const newAnnouncements = data.communityProfileConsolidatedNotifications
          .notifications as CommunityConsolidatedNotification[]

        setLabAnnouncements((prevAnnouncements) => {
          const allAnnouncements = [...prevAnnouncements, ...newAnnouncements]
          const uniqueAnnouncements = Array.from(
            new Map(allAnnouncements.map((a) => [a.id, a])).values()
          )
          return uniqueAnnouncements
        })
        isLabFetchInProgress.current = false
      }
    })
  }, [page, currentUser?.labSubscriptionActive, currentUser?.id])

  const isMain = activeMenu === 'MAIN'
  const isLab = activeMenu === 'LAB'

  if ((isMain && !mainAnnouncements) || (isLab && !labAnnouncements))
    return <></>

  const isAnyMainAnnouncements = Boolean(mainAnnouncements.length)
  const isAnyLabAnnouncements = Boolean(labAnnouncements.length)

  const notReadMainNumber =
    mainAnnouncements.filter((notice: Announcement) => !notice.read).length +
    Number(showLabBenefit)
  const notReadLabNumber =
    (data?.communityProfileConsolidatedNotifications.totalUnread || 0) +
    Number(showLabBenefit)

  const totalUnreadNumber = notReadLabNumber + notReadMainNumber

  const totalLabNumber =
    data?.communityProfileConsolidatedNotifications.total || 0

  const loadMoreLabAnnouncements = () => {
    if (
      !isLabFetchInProgress.current &&
      labAnnouncements.length < totalLabNumber
    ) {
      setPage((page) => page + 1)
    }
    isLabFetchInProgress.current = true
  }

  const isLoading = labAnnouncements.length < totalLabNumber

  return (
    <div ref={ref}>
      <NotificationBell
        isAnyAnnouncements={isAnyMainAnnouncements || isAnyLabAnnouncements}
        unreadAnnouncementCount={totalUnreadNumber}
        onClick={() => setIsDropdownOpen((open) => !open)}
      />
      {isDropdownOpen && (
        <Dropdown
          userId={currentUser.id}
          announcements={isMain ? mainAnnouncements : labAnnouncements}
          isRemoveReadFilter={isRemoveReadFilter}
          setIsRemoveReadFilter={setIsRemoveReadFilter}
          activeMenu={activeMenu}
          setActiveMenu={setActiveMenu}
          unreadLabAnnouncementsCount={notReadLabNumber}
          unreadMainAnnouncementsCount={notReadMainNumber}
          loadMoreLabAnnouncements={loadMoreLabAnnouncements}
          isLabLoading={isLoading}
          setLabAnnouncements={setLabAnnouncements}
          showLabBenefit={showLabBenefit}
        />
      )}
    </div>
  )
}

export default Announcements
